// ET-canonical day logic for the web client.
//
// CLAUDE.md critical rule #7: America/New_York is canonical for ALL
// day-boundary logic (daily-set doc IDs, streak comparisons, dedup keys).
// NEVER use the browser's local date for day identity — a user in London
// or LA must resolve the SAME dailySet doc ID as the server's
// `toETDateString` (functions/src/helpers.ts).
//
// Mirror of functions/src/helpers.ts toETDateString: en-CA formats as
// YYYY-MM-DD, and timeZone: 'America/New_York' forces ET regardless of
// where the browser is.

/**
 * The ET date string (YYYY-MM-DD) for a given instant (default: now).
 * This is the exact `dailySets/{id}` document ID the server writes.
 */
export function toETDateString(date = new Date()) {
  // Mirror functions/src/helpers.ts EXACTLY (same call, same args) so the web
  // and server always resolve the identical dailySets doc id. en-CA's default
  // date format is ISO yyyy-MM-dd, zero-padded — a wrong format here would
  // fetch the wrong doc and blank /play.
  return date.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

// The daily set rolls at 3am ET (= midnight PT), NOT ET midnight — so a
// west-coast player keeps their set until their own midnight.
const PLAY_DAY_OFFSET_HOURS = 3;

/**
 * The active PLAY-DAY ET date string (YYYY-MM-DD) — the day whose daily set is
 * currently in play. Mirror of functions/src/helpers.ts `playDayString`
 * (PLAY_DAY_OFFSET_HOURS=3) and iOS `PlayDay`; keep the 3h offset in lockstep.
 *
 * CLAUDE.md rule #7 + the day-boundary play-day rule: use THIS — not
 * `toETDateString` — for the daily-set doc id, the question-share-bonus gate,
 * streak day, and dedup keys. Using ET midnight for the set showed PT players
 * the NEXT set 3h early overnight AND made the server's per-question share bonus
 * (gated on `dailySets/{playDayString}.questionIds`) reject the share with
 * `not-in-todays-set`, silently dropping the +6 coins between 12am–3am ET.
 */
export function playDayString(date = new Date()) {
  return toETDateString(new Date(date.getTime() - PLAY_DAY_OFFSET_HOURS * 3600 * 1000));
}

/**
 * A short human dateline for today in ET (e.g. "Sat, Jun 13") — the day the
 * current daily set belongs to. ET so it matches the dailySets doc id.
 */
export function etTodayLabel(date = new Date()) {
  return date.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * A human "resolves <when>" label for a question's estimated resolution
 * date, framed honestly: questions are created to resolve at LEAST the day
 * AFTER they're published, so we never imply same-day resolution. The
 * actual resolution processing runs at 5 PM ET on the scheduled day.
 *
 * @param {Date} resolutionDate
 */
export function resolvesLabel(resolutionDate) {
  if (!(resolutionDate instanceof Date) || isNaN(resolutionDate.getTime())) {
    return 'Resolves soon';
  }
  const today = toETDateString();
  const target = toETDateString(resolutionDate);
  if (target === today) {
    // Scheduled to resolve today — the 5 PM ET processing run.
    return 'Resolves today, 5 PM ET';
  }
  if (target < today) {
    // A past-due question whose result hasn't posted yet — don't keep claiming
    // it "resolves today" once that day has passed (audit B6).
    return 'Awaiting result';
  }
  // Tomorrow vs. a weekday name vs. a full date.
  const tomorrow = toETDateString(new Date(Date.now() + 86400000));
  if (target === tomorrow) return 'Resolves tomorrow';

  const withinAWeek = new Date(Date.now() + 6 * 86400000);
  if (target <= toETDateString(withinAWeek)) {
    const weekday = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'long',
    }).format(resolutionDate);
    return `Resolves ${weekday}`;
  }
  const md = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
  }).format(resolutionDate);
  return `Resolves ${md}`;
}

/**
 * Milliseconds until the next play-day roll — the next 3:00 AM ET instant,
 * when `playDayString()` ticks over to the next day. R1 (Tom 2026-07-07):
 * powers the live "New six in Xh Ym" countdown (NextSixCountdown), replacing
 * the wrong static "midnight ET" copy (the roll is 3 AM ET — rule 12).
 *
 * DST-SAFE by construction: derives the current ET wall-clock hour/minute/
 * second via Intl (same technique as toETDateString/playDayString above) —
 * never a fixed UTC offset.
 *
 * Simplification: rather than resolving the exact next-3am Date instant
 * (which would need a timezone-aware date library to get exactly right
 * across a DST transition), this computes "seconds since the most recent
 * 3:00 AM ET" from the wall-clock time-of-day and returns the 24h
 * complement. On the two DST-transition days this can be off by up to ±1h
 * for the hours spanning the transition — acceptable because the display
 * re-ticks every 60s (see NextSixCountdown) and self-corrects on the very
 * next tick, so a transient ±1h twice a year beats shipping a tz library.
 */
export function msUntilNextPlayDayRoll(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(now);
  const partVal = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const secondsSinceMidnight = partVal('hour') * 3600 + partVal('minute') * 60 + partVal('second');
  const rollSeconds = PLAY_DAY_OFFSET_HOURS * 3600; // 3:00 AM ET
  const secondsSinceRoll =
    secondsSinceMidnight >= rollSeconds
      ? secondsSinceMidnight - rollSeconds
      : secondsSinceMidnight + (86400 - rollSeconds);
  return (86400 - secondsSinceRoll) * 1000;
}

/**
 * "5h 12m" (or "12m" under an hour, "<1m" floor) — the display format for
 * `msUntilNextPlayDayRoll`. Minute-granularity by design; the countdown
 * re-ticks every 60s (NextSixCountdown), so seconds would just flicker.
 */
export function formatCountdown(ms) {
  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) return '<1m';
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h < 1 ? `${m}m` : `${h}h ${m}m`;
}

// ── The weekly games' clock ──────────────────────────────────────────────────
// Lockstep with iOS PlayDay.swift:109-133 and functions/src/leagues.ts:78. All
// three copies must agree; this is the web one.

/**
 * The play week's Sunday for a play-day, as `YYYY-MM-DD`.
 *
 * Weeks are Sun–Sat and hang off the PLAY day (which rolls at 3 AM ET), not the
 * calendar day — so this takes a play-day string rather than a Date.
 */
export function weekKey(day = playDayString()) {
  const [y, m, d] = String(day).split('-').map(Number);
  if (!y || !m || !d) return '';
  // Noon UTC keeps the arithmetic clear of DST edges.
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  dt.setUTCDate(dt.getUTCDate() - dt.getUTCDay());
  return dt.toISOString().slice(0, 10);
}

/**
 * The first week any weekly game scores. H2H week one, the group week's floor,
 * and LEAGUE_EPOCH_WEEK are all this date — all three games start together
 * (functions/src/groupWeeks.ts:70).
 */
export const LEAGUE_EPOCH_WEEK = '2026-07-19';

/**
 * Is a weekly game scoring the week we're in right now?
 *
 * False before the epoch — the server skips those weeks entirely, so any number
 * a face shows for them is measuring something the game isn't playing. This
 * exists because of a real bug: the League face drew each player's own rolling
 * Sun–Sat count under "Week 1" as though it were the game's score, and Tom hit
 * it on device (2026-07-15: "where did the 15 numbers come from? i thought this
 * starts sunday"). It was right about the number and wrong about the game.
 *
 * Faces that read a SERVER matchup denorm (h2hThisWeek, groupThisWeek) don't
 * need this — the server simply hasn't written those weeks. It's the faces that
 * read a player's own rolling counter that have to ask.
 */
export function leagueIsScoring(day = playDayString()) {
  return weekKey(day) >= LEAGUE_EPOCH_WEEK;
}

/**
 * Read a server weekly counter THROUGH the week stamp shipped with it: this
 * week's value, or 0 if the pair belongs to an earlier week.
 *
 * Every weekly counter the server writes (`weeklyCorrect` on the user doc and
 * on the League payload) moves only when that user's OWN answers resolve. So
 * someone who last played Thursday carries Thursday's count under Thursday's
 * stamp until they play again, and a reader that trusts the count alone shows
 * last week's score all through Sunday and Monday. A stale stamp is not missing
 * data — it means nothing has resolved for them this week, which is a true 0.
 *
 * Lives here next to `weekKey` so the counter and the rule for reading it stay
 * in one place: two decoders of the same server field must not each keep their
 * own copy of this comparison.
 */
export function weeklyScore(correct, stamp) {
  return stamp === weekKey() ? (correct || 0) : 0;
}
